import { useState, FormEvent } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setStatusMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d99lglfk7gok24d4aea0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('Thank you for subscribing! Check your inbox for the latest updates.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        setStatus('error');
        setStatusMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-accent-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 rounded-2xl bg-accent-100 flex items-center justify-center">
            <i className="ri-mail-send-line text-accent-600 text-2xl w-6 h-6 flex items-center justify-center"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Stay Updated with Hipobuy Sheet
          </h2>
          <p className="text-foreground-600 text-sm md:text-base mb-6">
            Get the latest product discoveries, spreadsheet guides, and shopping tips delivered to your inbox.
            No spam, unsubscribe anytime.
          </p>

          <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <div className="relative flex-1">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full px-5 py-3 rounded-full bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200 transition-all"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honey"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="whitespace-nowrap px-6 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {status !== 'idle' && (
            <p className={`mt-4 text-sm ${status === 'success' ? 'text-accent-700' : 'text-red-600'}`}>
              {statusMessage}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}